import React, { useCallback, useEffect, useRef } from 'react';

import { useRouter } from 'next/router.js';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { CUSTOM_COLOR_FILTER } from '~/gridv1_1/constants.js';
import useUnifiedProductGridData from '~/gridv1_1/hooks/useUnifiedProductGridData';
import { updateFiltersInUrlParams } from '~/gridv1_1/utils/filters.js';
import { QueryParamBehavior } from '~/gridv1_1/utils/routing';
import { scrollToTopOfGrid } from '~/gridv1_1/utils/scroll.js';
import ColorWheel from '~/shared/components/ColorWheel/ColorWheel.js';
import useColorActions from '~/shared/utils/useColorActions.js';
import { useBreakpoint } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl/index.js';

const DesktopGridColorWheelFilterWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const ColorWheelFilterError = styled.p`
  color: red;
  text-align: center;
  margin-bottom: 15px;
`;

const ColorBox = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: none;
  position: relative;
  top: 1px;
  margin-right: 4px;
  background-color: ${(props) => props.color};
`;

function ColorWheelFilterItem({ color }) {
  return (
    <>
      <ColorBox color={`rgb(${color.r}, ${color.g}, ${color.b})`} />
      <FormattedMessage
        defaultMessage="Custom Color"
        id="site_product_grid.custom_color_filter_label"
      />
    </>
  );
}

ColorWheelFilterItem.propTypes = {
  color: PropTypes.shape({
    r: PropTypes.number.isRequired,
    g: PropTypes.number.isRequired,
    b: PropTypes.number.isRequired,
  }).isRequired,
};

function DesktopGridColorWheelFilter() {
  const { customFacetFilters, setCustomFacetFilters } =
    useUnifiedProductGridData();
  const colorActions = useColorActions();
  const router = useRouter();
  const { isMobile } = useBreakpoint();
  const query = router.asPath.split('?')[1];
  const hasInitializedRef = useRef(false);

  const setSelectedColor = useCallback(
    async (selectedColor) => {
      const nearestColors =
        await colorActions.getNearestColorTagsByColorValues(selectedColor);
      const payload = nearestColors?.payload;
      if (!payload) {
        return;
      }

      const tagIdList = payload.map((c) => c.tagId);

      updateFiltersInUrlParams({
        facetName: CUSTOM_COLOR_FILTER.name,
        facetOption: Object.values(selectedColor).join(','),
        router,
        queryParamBehavior: QueryParamBehavior.REPLACE,
      });

      scrollToTopOfGrid({ isMobile });

      setCustomFacetFilters((prev) => ({
        ...prev,
        [CUSTOM_COLOR_FILTER.name]: {
          ...CUSTOM_COLOR_FILTER,
          label: <ColorWheelFilterItem color={selectedColor} />,
          filters: { tag_id_list: tagIdList },
          hasError: false,
        },
      }));
    },
    [colorActions, router, isMobile, setCustomFacetFilters]
  );

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    const urlParams = new URLSearchParams(query);
    const facetValue = urlParams.get(CUSTOM_COLOR_FILTER.name);

    if (facetValue) {
      const [r, g, b] = facetValue.split(',').map((v) => parseInt(v, 10));
      const selectedColor = { r, g, b };

      if (!customFacetFilters[CUSTOM_COLOR_FILTER.name]?.hasError) {
        setSelectedColor(selectedColor);
      } else {
        updateFiltersInUrlParams({
          facetName: CUSTOM_COLOR_FILTER.name,
          facetOption: [],
          router,
          queryParamBehavior: QueryParamBehavior.REPLACE,
        });
      }
    }
  }, [customFacetFilters, query, setSelectedColor, router]);

  return (
    <DesktopGridColorWheelFilterWrapper>
      {customFacetFilters[CUSTOM_COLOR_FILTER.name]?.hasError && (
        <ColorWheelFilterError>
          <FormattedMessage
            id="site_product_grid.custom_color_filter_error"
            defaultMessage="No matches. Please try another color."
          />
        </ColorWheelFilterError>
      )}
      <ColorWheel
        lightness={50}
        onColorSelect={(rgb) => setSelectedColor(rgb)}
      />
    </DesktopGridColorWheelFilterWrapper>
  );
}

export default DesktopGridColorWheelFilter;
